import { readFieldValue } from '../../components/FormComponents/dynamicForm/field_config_schema';
import { APPLICATION_TYPES, CustomerEntityTypeOptions } from '../../config/constants';
import { API_FIELD } from './config/api_field_names';
import { ASSET_FIELD, ASSET_LOCATION_TYPE, BILLING_ADDRESS_PREFIX, CUSTOMER_ASPIRE_FIELD, CUSTOMER_FIELD, FINANCE_FIELD, GUARANTOR_ADDRESS_PREFIX, GUARANTOR_ASPIRE_FIELD, GUARANTOR_FIELD, GUARANTOR_HOME_ADDRESS_PREFIX, GUARANTOR_TYPE, getCustomerRecordLabel, MAIN_ADDRESS_PREFIX, readCustomerAssetLocation, SIGNER_GUARANTOR_MATCHED_CONTACT_FIELD, } from './config/create_application_schema';
import { buildAddressFieldName, parseAmount } from './config/form_field_factories';
import { asText } from './service_helpers';
var BUS_OR_IND = {
    Individual: 'I',
    Business: 'B',
};
var NEW_ASSET_CONDITION = 'NEW';
var API_APPLICATION_TYPE = {
    NOR: 'NOR',
    TLC: 'TLC',
    SCH: 'SCH',
};
export var toApiApplicationType = function (applicationType) {
    if (applicationType === APPLICATION_TYPES.NOR) {
        return API_APPLICATION_TYPE.NOR;
    }
    if (applicationType === APPLICATION_TYPES.SCH) {
        return API_APPLICATION_TYPE.SCH;
    }
    return API_APPLICATION_TYPE.TLC;
};
var isRentalFleetType = function (applicationType) {
    return applicationType === APPLICATION_TYPES.DORF;
};
var isFlagged = function (value) {
    return value === true || Number(value) === 1 || String(value) === 'true';
};
var read = function (values, schemaName, apiFieldName) {
    return asText(readFieldValue(values, schemaName, apiFieldName));
};
var put = function (record, key, value) {
    if (value === undefined || value === '' || value === null) {
        return;
    }
    record[key] = value;
};
var putAlways = function (record, key, value, whenEmpty) {
    if (whenEmpty === void 0) { whenEmpty = ''; }
    record[key] = value === undefined || value === '' || value === null ? whenEmpty : value;
};
var toPhoneDigits = function (value) {
    return asText(value).replace(/\D/g, '');
};
var toRawTaxId = function (value) {
    return asText(value).replace(/[^0-9A-Za-z]/g, '');
};
var readAddress = function (values, formPrefix, apiPrefix, includeContact) {
    var formField = function (suffix) { return buildAddressFieldName(formPrefix, suffix); };
    var apiField = function (suffix) { return "".concat(apiPrefix).concat(suffix); };
    var record = {};
    ['Address', 'Country', 'State', 'City', 'PostalCode'].forEach(function (suffix) {
        put(record, apiField(suffix), read(values, formField(suffix), apiField(suffix)));
    });
    if (includeContact) {
        put(record, apiField(API_FIELD.Email), read(values, formField('Email'), apiField('Email')));
        put(record, apiField(API_FIELD.PhoneNumber), toPhoneDigits(read(values, formField('Phone'), apiField('PhoneNumber'))));
    }
    return record;
};
var toEntityDescription = function (entityCode) { var _a, _b; return (_b = (_a = CustomerEntityTypeOptions.find(function (option) { return option.Code === entityCode; })) === null || _a === void 0 ? void 0 : _a.Description) !== null && _b !== void 0 ? _b : ''; };
var SOLE_PROPRIETORSHIP = 'Sole Proprietorship';
var isSoleProprietorship = function (customer) {
    return customer.CompanyDesc === SOLE_PROPRIETORSHIP;
};
var buildCustomerInfo = function (customers, isConsumer) {
    return customers.map(function (customer, index) {
        var record = {};
        var entityCode = read(customer, CUSTOMER_FIELD.EntityType, API_FIELD.EntityType);
        var entityDescription = toEntityDescription(entityCode);
        var customerAspireId = asText(customer[CUSTOMER_ASPIRE_FIELD.CustomerAspireId]);
        record.CustomerIndex = customerAspireId || "customer-".concat(index + 1);
        record.IsPrimary = index === 0 ? 1 : 0;
        var veteranStatus = read(customer, CUSTOMER_FIELD.VeteranStatus, API_FIELD.VeteranStatus);
        if (veteranStatus === '1' || veteranStatus === '0') {
            record.VeteranStatus = Number(veteranStatus);
        }
        record.BusOrInd =
            isConsumer || entityDescription === SOLE_PROPRIETORSHIP
                ? BUS_OR_IND.Individual
                : BUS_OR_IND.Business;
        put(record, API_FIELD.EntityType, entityCode);
        put(record, 'CompanyDesc', entityDescription);
        put(record, API_FIELD.LegalRegisteredName, read(customer, CUSTOMER_FIELD.LegalRegisteredName, API_FIELD.LegalRegisteredName));
        putAlways(record, API_FIELD.DbaName, read(customer, CUSTOMER_FIELD.DbaName, API_FIELD.DbaName), null);
        put(record, API_FIELD.CustomerCountry, read(customer, CUSTOMER_FIELD.Country, API_FIELD.CustomerCountry));
        put(record, API_FIELD.CustomerState, read(customer, CUSTOMER_FIELD.StateOfIncorporation, API_FIELD.CustomerState));
        putAlways(record, API_FIELD.DateBusinessIncorporated, read(customer, CUSTOMER_FIELD.DateBusinessIncorporated, API_FIELD.DateBusinessIncorporated), null);
        putAlways(record, 'CurrentOwnerShipDate', '', null);
        put(record, API_FIELD.TaxpayerId, toRawTaxId(read(customer, CUSTOMER_FIELD.FedTaxId, API_FIELD.TaxpayerId) ||
            read(customer, CUSTOMER_FIELD.TaxpayerId, API_FIELD.TaxpayerId)));
        var grossRevenue = read(customer, CUSTOMER_FIELD.GrossAnnualRevenue, API_FIELD.GrossRevenue);
        if (grossRevenue) {
            record.GrossRevenue = parseAmount(grossRevenue);
        }
        var taxExempt = readFieldValue(customer, CUSTOMER_FIELD.TaxExempt, API_FIELD.TaxExempt);
        if (asText(taxExempt) !== '') {
            record.TaxExempt = isFlagged(taxExempt);
        }
        Object.assign(record, readAddress(customer, MAIN_ADDRESS_PREFIX, '', true));
        var isBillingSameAsMain = isFlagged(customer[CUSTOMER_FIELD.BillingSameAsMain]);
        record.IsBillToLocSameAsCustLoc = isBillingSameAsMain ? 1 : 0;
        if (!isBillingSameAsMain) {
            Object.assign(record, readAddress(customer, BILLING_ADDRESS_PREFIX, 'Billing', true));
        }
        var isIndividual = record.BusOrInd === BUS_OR_IND.Individual;
        putAlways(record, API_FIELD.FirstName, isIndividual ? read(customer, CUSTOMER_FIELD.SignerFirstName, API_FIELD.FirstName) : '');
        putAlways(record, API_FIELD.MiddleName, isIndividual ? read(customer, CUSTOMER_FIELD.SignerMiddleName, API_FIELD.MiddleName) : '');
        putAlways(record, API_FIELD.LastName, isIndividual ? read(customer, CUSTOMER_FIELD.SignerLastName, API_FIELD.LastName) : '');
        putAlways(record, API_FIELD.Suffix, isIndividual ? read(customer, CUSTOMER_FIELD.SignerSuffix, API_FIELD.Suffix) : '');
        putAlways(record, API_FIELD.BusinessTitle, isIndividual ? read(customer, CUSTOMER_FIELD.BusinessTitle, API_FIELD.BusinessTitle) : '');
        putAlways(record, API_FIELD.DateOfBirth, isIndividual ? read(customer, CUSTOMER_FIELD.DateOfBirth, API_FIELD.DateOfBirth) : '');
        put(record, 'CustomerAspireId', asText(customer[CUSTOMER_ASPIRE_FIELD.CustomerAspireId]));
        put(record, 'CustomerAspireOId', asText(customer[CUSTOMER_ASPIRE_FIELD.CustomerAspireOId]));
        put(record, 'CustomerAspireRecordId', asText(customer[CUSTOMER_ASPIRE_FIELD.CustomerAspireRecordId]));
        put(record, 'LocationAspireOId', asText(customer[CUSTOMER_ASPIRE_FIELD.LocationAspireOId]));
        var billToLocation = asText(customer[CUSTOMER_ASPIRE_FIELD.BillToLocationAspireOId]);
        var isRedundantBillToLocation = !isBillingSameAsMain && billToLocation === record.LocationAspireOId;
        if (billToLocation && !isRedundantBillToLocation) {
            record.BillToLocationAspireOId = billToLocation;
        }
        return record;
    });
};
var buildContactInfo = function (customers, customerRecords, isNorApplication) {
    var contacts = [];
    customers.forEach(function (customer, index) {
        var _a, _b, _c;
        var firstName = read(customer, CUSTOMER_FIELD.SignerFirstName, API_FIELD.FirstName);
        var lastName = read(customer, CUSTOMER_FIELD.SignerLastName, API_FIELD.LastName);
        var email = read(customer, CUSTOMER_FIELD.SignerEmail, API_FIELD.Email);
        if (!firstName && !lastName && !email) {
            return;
        }
        var contact = {
            CustomerIndex: (_a = customerRecords[index]) === null || _a === void 0 ? void 0 : _a.CustomerIndex,
            AccType: (_c = (_b = customerRecords[index]) === null || _b === void 0 ? void 0 : _b.BusOrInd) !== null && _c !== void 0 ? _c : BUS_OR_IND.Business,
            DocSigner: true,
            IsPrimary: isNorApplication ? (contacts.length === 0 ? 1 : 0) : 1,
            GIndex: '',
        };
        put(contact, 'ContactAspireOId', asText(customer[CUSTOMER_ASPIRE_FIELD.SignerContactAspireOId]));
        put(contact, 'CustomerAspireId', asText(customer[CUSTOMER_ASPIRE_FIELD.CustomerAspireId]));
        putAlways(contact, 'CustomerAssociation', '');
        putAlways(contact, API_FIELD.FirstName, firstName);
        put(contact, API_FIELD.MiddleName, read(customer, CUSTOMER_FIELD.SignerMiddleName, API_FIELD.MiddleName));
        putAlways(contact, API_FIELD.LastName, lastName);
        putAlways(contact, API_FIELD.Suffix, read(customer, CUSTOMER_FIELD.SignerSuffix, API_FIELD.Suffix));
        putAlways(contact, API_FIELD.Title, read(customer, CUSTOMER_FIELD.SignerTitle, API_FIELD.Title));
        putAlways(contact, API_FIELD.Email, email);
        putAlways(contact, API_FIELD.PhoneNumber, toPhoneDigits(read(customer, CUSTOMER_FIELD.SignerPhone, API_FIELD.PhoneNumber)));
        contacts.push(contact);
    });
    return contacts;
};
var buildGuarantorSignerContact = function (guarantor, guarantorRecord) {
    var firstName = read(guarantor, GUARANTOR_FIELD.SignerFirstName, API_FIELD.FirstName);
    var lastName = read(guarantor, GUARANTOR_FIELD.SignerLastName, API_FIELD.LastName);
    var email = read(guarantor, GUARANTOR_FIELD.SignerEmail, API_FIELD.Email);
    if (!firstName && !lastName && !email) {
        return null;
    }
    var contact = {
        GIndex: guarantorRecord.GIndex,
        CustomerIndex: '',
        AccType: BUS_OR_IND.Business,
        DocSigner: true,
        IsPrimary: 0,
    };
    put(contact, 'ContactAspireOId', asText(guarantor[GUARANTOR_ASPIRE_FIELD.SignerContactAspireOId]));
    put(contact, 'GuarantorAspireOId', asText(guarantor[GUARANTOR_ASPIRE_FIELD.GuarantorAspireOId]));
    putAlways(contact, 'CustomerAssociation', '');
    putAlways(contact, API_FIELD.FirstName, firstName);
    put(contact, API_FIELD.MiddleName, read(guarantor, GUARANTOR_FIELD.SignerMiddleName, API_FIELD.MiddleName));
    putAlways(contact, API_FIELD.LastName, lastName);
    putAlways(contact, API_FIELD.Suffix, read(guarantor, GUARANTOR_FIELD.SignerSuffix, API_FIELD.Suffix));
    putAlways(contact, API_FIELD.Title, read(guarantor, GUARANTOR_FIELD.SignerTitle, API_FIELD.Title));
    putAlways(contact, API_FIELD.Email, email);
    putAlways(contact, API_FIELD.PhoneNumber, toPhoneDigits(read(guarantor, GUARANTOR_FIELD.SignerPhone, API_FIELD.PhoneNumber)));
    return contact;
};
var readSignerGuarantorDetail = function (customer, matchedField, signerField, apiFieldName) { return asText(customer[matchedField]) || read(customer, signerField, apiFieldName); };
var buildSignerGuarantor = function (customer, customerRecord, index, existingGuarantorCount) {
    if (!isFlagged(customer[CUSTOMER_FIELD.DocumentSignerSameAsGuarantor])) {
        return null;
    }
    var record = {
        GIndex: "guarantor-signer-".concat(index + 1),
        GurType: GUARANTOR_TYPE.Individual,
        CustomerIndex: customerRecord.CustomerIndex,
        DocSigner: true,
        IsPrimary: existingGuarantorCount === 0 ? 1 : 0,
    };
    put(record, API_FIELD.FirstName, readSignerGuarantorDetail(customer, SIGNER_GUARANTOR_MATCHED_CONTACT_FIELD.FirstName, CUSTOMER_FIELD.SignerFirstName, API_FIELD.FirstName));
    put(record, API_FIELD.MiddleName, read(customer, CUSTOMER_FIELD.SignerMiddleName, API_FIELD.MiddleName));
    put(record, API_FIELD.LastName, readSignerGuarantorDetail(customer, SIGNER_GUARANTOR_MATCHED_CONTACT_FIELD.LastName, CUSTOMER_FIELD.SignerLastName, API_FIELD.LastName));
    put(record, API_FIELD.Suffix, read(customer, CUSTOMER_FIELD.SignerSuffix, API_FIELD.Suffix));
    put(record, API_FIELD.Email, readSignerGuarantorDetail(customer, SIGNER_GUARANTOR_MATCHED_CONTACT_FIELD.Email, CUSTOMER_FIELD.SignerEmail, API_FIELD.Email));
    put(record, API_FIELD.PhoneNumber, toPhoneDigits(readSignerGuarantorDetail(customer, SIGNER_GUARANTOR_MATCHED_CONTACT_FIELD.PhoneNumber, CUSTOMER_FIELD.SignerPhone, API_FIELD.PhoneNumber)));
    put(record, API_FIELD.BusinessTitle, read(customer, CUSTOMER_FIELD.BusinessTitle, API_FIELD.BusinessTitle));
    put(record, API_FIELD.TaxpayerId, toRawTaxId(read(customer, CUSTOMER_FIELD.TaxpayerId, API_FIELD.TaxpayerId)));
    put(record, API_FIELD.DateOfBirth, read(customer, CUSTOMER_FIELD.DateOfBirth, API_FIELD.DateOfBirth));
    var ownership = read(customer, CUSTOMER_FIELD.PercentOwnership, API_FIELD.OwnershipPercentage);
    if (ownership) {
        record.OwnershipPercentage = parseAmount(ownership);
    }
    put(record, 'GuarantorAspireOId', asText(customer[GUARANTOR_ASPIRE_FIELD.GuarantorAspireOId]));
    put(record, 'GuarantorAspireRecordId', asText(customer[GUARANTOR_ASPIRE_FIELD.GuarantorAspireRecordId]));
    put(record, 'LocationAspireOId', asText(customer[GUARANTOR_ASPIRE_FIELD.LocationAspireOId]));
    put(record, 'LocationAspireRecordId', asText(customer[GUARANTOR_ASPIRE_FIELD.LocationAspireRecordId]));
    Object.assign(record, readAddress(customer, GUARANTOR_ADDRESS_PREFIX, '', false));
    return record;
};
var buildSignerGuarantorContact = function (customer, guarantorRecord, isPrimary) {
    var contact = {
        GIndex: guarantorRecord.GIndex,
        CustomerIndex: '',
        DocSigner: true,
        IsPrimary: isPrimary,
    };
    put(contact, 'ContactAspireOId', asText(customer[GUARANTOR_ASPIRE_FIELD.SignerContactAspireOId]));
    put(contact, 'GuarantorAspireOId', asText(customer[GUARANTOR_ASPIRE_FIELD.GuarantorAspireOId]));
    putAlways(contact, API_FIELD.FirstName, readSignerGuarantorDetail(customer, SIGNER_GUARANTOR_MATCHED_CONTACT_FIELD.FirstName, CUSTOMER_FIELD.SignerFirstName, API_FIELD.FirstName));
    putAlways(contact, API_FIELD.MiddleName, read(customer, CUSTOMER_FIELD.SignerMiddleName, API_FIELD.MiddleName));
    putAlways(contact, API_FIELD.LastName, readSignerGuarantorDetail(customer, SIGNER_GUARANTOR_MATCHED_CONTACT_FIELD.LastName, CUSTOMER_FIELD.SignerLastName, API_FIELD.LastName));
    putAlways(contact, API_FIELD.Suffix, read(customer, CUSTOMER_FIELD.SignerSuffix, API_FIELD.Suffix));
    putAlways(contact, API_FIELD.Email, readSignerGuarantorDetail(customer, SIGNER_GUARANTOR_MATCHED_CONTACT_FIELD.Email, CUSTOMER_FIELD.SignerEmail, API_FIELD.Email));
    putAlways(contact, API_FIELD.PhoneNumber, toPhoneDigits(readSignerGuarantorDetail(customer, SIGNER_GUARANTOR_MATCHED_CONTACT_FIELD.PhoneNumber, CUSTOMER_FIELD.SignerPhone, API_FIELD.PhoneNumber)));
    putAlways(contact, API_FIELD.Title, 'INDIVIDUAL');
    return contact;
};
var readGuarantorType = function (guarantor) {
    return read(guarantor, GUARANTOR_FIELD.GuarantorType, 'GurType') || GUARANTOR_TYPE.Individual;
};
var buildGuarantor = function (guarantor, index) {
    var guarantorType = readGuarantorType(guarantor);
    var isBusiness = guarantorType === GUARANTOR_TYPE.Business;
    var record = {
        GIndex: "guarantor-".concat(index + 1),
        GurType: guarantorType,
        IsPrimary: index === 0 ? 1 : 0,
        DocSigner: false,
    };
    put(record, 'CustomerIndex', read(guarantor, GUARANTOR_FIELD.CustomerAssociation, 'CustomerIndex'));
    if (isBusiness) {
        put(record, API_FIELD.BusinessName, read(guarantor, GUARANTOR_FIELD.BusinessName, API_FIELD.BusinessName));
        put(record, API_FIELD.TaxpayerId, read(guarantor, GUARANTOR_FIELD.BusinessFedTaxId, API_FIELD.TaxpayerId));
        var entityCode = read(guarantor, GUARANTOR_FIELD.BusinessEntityType, API_FIELD.EntityType);
        put(record, API_FIELD.EntityType, entityCode);
        put(record, 'CompanyDesc', toEntityDescription(entityCode));
        put(record, API_FIELD.BusinessTitle, read(guarantor, GUARANTOR_FIELD.BusinessTitle, API_FIELD.BusinessTitle));
    }
    else {
        put(record, API_FIELD.FirstName, read(guarantor, GUARANTOR_FIELD.FirstName, API_FIELD.FirstName));
        put(record, API_FIELD.MiddleName, read(guarantor, GUARANTOR_FIELD.MiddleName, API_FIELD.MiddleName));
        put(record, API_FIELD.LastName, read(guarantor, GUARANTOR_FIELD.LastName, API_FIELD.LastName));
        put(record, API_FIELD.Suffix, read(guarantor, GUARANTOR_FIELD.Suffix, API_FIELD.Suffix));
        put(record, API_FIELD.TaxpayerId, toRawTaxId(read(guarantor, GUARANTOR_FIELD.TaxpayerId, API_FIELD.TaxpayerId)));
        put(record, API_FIELD.DateOfBirth, read(guarantor, GUARANTOR_FIELD.DateOfBirth, API_FIELD.DateOfBirth));
    }
    var collectsOwnership = isBusiness || isFlagged(guarantor[GUARANTOR_FIELD.HasOwnership]);
    if (collectsOwnership) {
        var ownership = read(guarantor, GUARANTOR_FIELD.OwnershipPercentage, API_FIELD.OwnershipPercentage);
        if (ownership) {
            record.OwnershipPercentage = parseAmount(ownership);
        }
        if (!isBusiness) {
            put(record, API_FIELD.BusinessTitle, read(guarantor, GUARANTOR_FIELD.BusinessTitle, API_FIELD.BusinessTitle));
        }
    }
    put(record, 'GuarantorAspireOId', asText(guarantor[GUARANTOR_ASPIRE_FIELD.GuarantorAspireOId]));
    put(record, 'GuarantorAspireRecordId', asText(guarantor[GUARANTOR_ASPIRE_FIELD.GuarantorAspireRecordId]));
    put(record, 'LocationAspireOId', asText(guarantor[GUARANTOR_ASPIRE_FIELD.LocationAspireOId]));
    put(record, 'LocationAspireRecordId', asText(guarantor[GUARANTOR_ASPIRE_FIELD.LocationAspireRecordId]));
    Object.assign(record, readAddress(guarantor, GUARANTOR_HOME_ADDRESS_PREFIX, '', true));
    return record;
};
var buildAssetLocation = function (asset, primaryCustomer, choices) {
    var _a, _b;
    var chosenLabel = asText(asset[ASSET_FIELD.Location]);
    var chosen = choices.find(function (choice) { return choice.label === chosenLabel; });
    var location = (_a = chosen === null || chosen === void 0 ? void 0 : chosen.location) !== null && _a !== void 0 ? _a : readCustomerAssetLocation(primaryCustomer, MAIN_ADDRESS_PREFIX);
    var record = {
        LocationType: (_b = chosen === null || chosen === void 0 ? void 0 : chosen.locationType) !== null && _b !== void 0 ? _b : ASSET_LOCATION_TYPE.Customer,
    };
    put(record, 'LocationName', location.locationName);
    put(record, 'StreetName', location.address);
    put(record, 'City', location.city);
    put(record, 'State', location.state);
    put(record, 'Country', location.country);
    put(record, 'PostalCode', location.postalCode);
    return record;
};
var buildAssetInfo = function (assets, primaryCustomer, locationChoices, isPrimaryCustomerVeteran) {
    if (isPrimaryCustomerVeteran === void 0) { isPrimaryCustomerVeteran = false; }
    return assets.map(function (asset) {
        var record = {};
        put(record, 'Code', read(asset, ASSET_FIELD.EquipmentType, 'EquipmentType'));
        put(record, 'Description', read(asset, ASSET_FIELD.Description, 'Description'));
        put(record, 'Manufacturer', read(asset, ASSET_FIELD.Manufacturer, 'Manufacturer'));
        putAlways(record, 'Model', read(asset, ASSET_FIELD.Model, 'Model'));
        put(record, API_FIELD.Year, read(asset, ASSET_FIELD.Year, API_FIELD.Year));
        putAlways(record, 'SerialNums', read(asset, ASSET_FIELD.SerialNumber, 'SerialNums'));
        record.ListPrice = 0;
        record.Odometer = Number(read(asset, ASSET_FIELD.Odometer, 'Odometer') || 0);
        record.ConversionCost = parseAmount(readFieldValue(asset, ASSET_FIELD.ConversionCost, 'ConversionCost'));
        record.EquipmentCost = parseAmount(readFieldValue(asset, ASSET_FIELD.CostPerUnit, API_FIELD.EquipmentCost));
        var quantity = Number(read(asset, ASSET_FIELD.Quantity, API_FIELD.Quantity) || 0);
        record.Quantity = Number.isFinite(quantity) ? quantity : 0;
        record.IsNew =
            read(asset, ASSET_FIELD.NewOrUsed, 'IsNew').toUpperCase() === NEW_ASSET_CONDITION
                ? 1
                : 0;
        record.IsTitledAsset = isFlagged(asset[ASSET_FIELD.IsTitled]) ? 1 : 0;
        if (isPrimaryCustomerVeteran) {
            record.VASupport = isFlagged(asset[ASSET_FIELD.VASupport]) ? 1 : 0;
        }
        Object.assign(record, buildAssetLocation(asset, primaryCustomer, locationChoices));
        return record;
    });
};
var CONSUMER_MARKER = 'consumer';
var isConsumerProgram = function (programName) {
    return programName.toLowerCase().includes(CONSUMER_MARKER);
};
export var readPrimaryCustomerName = function (wizard) {
    var _a, _b, _c;
    var isConsumer = isConsumerProgram((_b = (_a = wizard.selection) === null || _a === void 0 ? void 0 : _a.program) !== null && _b !== void 0 ? _b : '');
    var primaryCustomer = buildCustomerInfo((_c = wizard.customers) !== null && _c !== void 0 ? _c : [], isConsumer)[0];
    if (!primaryCustomer) {
        return '';
    }
    var readName = function (key) { var _a; return String((_a = primaryCustomer[key]) !== null && _a !== void 0 ? _a : '').trim(); };
    if (primaryCustomer.BusOrInd === BUS_OR_IND.Business) {
        return readName(API_FIELD.LegalRegisteredName);
    }
    return [readName(API_FIELD.FirstName), readName(API_FIELD.LastName)]
        .filter(Boolean)
        .join(' ');
};
export var readSignerGuarantors = function (customers) {
    if (customers === void 0) { customers = []; }
    return customers.reduce(function (previews, customer, index) {
        if (!isFlagged(customer[CUSTOMER_FIELD.DocumentSignerSameAsGuarantor])) {
            return previews;
        }
        var name = [
            read(customer, CUSTOMER_FIELD.SignerFirstName, API_FIELD.FirstName),
            read(customer, CUSTOMER_FIELD.SignerMiddleName, API_FIELD.MiddleName),
            read(customer, CUSTOMER_FIELD.SignerLastName, API_FIELD.LastName),
            read(customer, CUSTOMER_FIELD.SignerSuffix, API_FIELD.Suffix),
        ]
            .filter(Boolean)
            .join(' ');
        previews.push({
            customerLabel: getCustomerRecordLabel(index),
            name: name,
            email: read(customer, CUSTOMER_FIELD.SignerEmail, API_FIELD.Email),
            phone: read(customer, CUSTOMER_FIELD.SignerPhone, API_FIELD.PhoneNumber),
            businessTitle: read(customer, CUSTOMER_FIELD.BusinessTitle, API_FIELD.BusinessTitle),
            taxpayerId: read(customer, CUSTOMER_FIELD.TaxpayerId, API_FIELD.TaxpayerId),
            dateOfBirth: read(customer, CUSTOMER_FIELD.DateOfBirth, API_FIELD.DateOfBirth),
            ownership: read(customer, CUSTOMER_FIELD.PercentOwnership, API_FIELD.OwnershipPercentage),
            address: ['Address', 'City', 'State', 'PostalCode', 'Country']
                .map(function (suffix) {
                var _a;
                return String((_a = customer[buildAddressFieldName(GUARANTOR_ADDRESS_PREFIX, suffix)]) !== null && _a !== void 0 ? _a : '').trim();
            })
                .filter(Boolean)
                .join(', '),
        });
        return previews;
    }, []);
};
var applyFinanceProductCode = function (payload, finance, financeProducts) {
    var financeProductCode = read(finance, FINANCE_FIELD.FinanceProduct, 'FinanceProductCode');
    put(payload, 'FinanceProductCode', financeProductCode);
    var financeProduct = (financeProducts !== null && financeProducts !== void 0 ? financeProducts : []).find(function (product) { return product.value === financeProductCode; });
    if (financeProduct) {
        payload.IsLoan = financeProduct.isLoan ? 1 : 0;
    }
    return financeProductCode;
};
export var buildCreateApplicationRequest = function (wizard) {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p;
    var applicationType = (_a = wizard.applicationType) !== null && _a !== void 0 ? _a : '';
    var apiApplicationType = toApiApplicationType(applicationType);
    var isLineOfCredit = apiApplicationType === API_APPLICATION_TYPE.TLC;
    var isSchedule = apiApplicationType === API_APPLICATION_TYPE.SCH;
    var isNor = apiApplicationType === API_APPLICATION_TYPE.NOR;
    var selection = wizard.selection;
    var programName = (_b = selection === null || selection === void 0 ? void 0 : selection.program) !== null && _b !== void 0 ? _b : '';
    var isConsumer = isConsumerProgram(programName);
    var payload = {
        IsDraft: 0,
        ApplicationType: apiApplicationType,
        IsDealerDisclaimerChecked: 1,
    };
    var draftApplicationId = Number((_c = wizard.applicationId) !== null && _c !== void 0 ? _c : '');
    if (Number.isInteger(draftApplicationId) && draftApplicationId > 0) {
        payload.ApplicationId = draftApplicationId;
    }
    if (isRentalFleetType(applicationType)) {
        payload.RentalFleet = 1;
    }
    if (isSchedule) {
        put(payload, 'LOCAspireOId', wizard.locAspireOId);
    }
    var customers = (_d = wizard.customers) !== null && _d !== void 0 ? _d : [];
    var customerRecords = buildCustomerInfo(customers, isConsumer);
    payload.CustomerInfo = customerRecords;
    var isPrimaryCustomerVeteran = ((_e = customerRecords[0]) === null || _e === void 0 ? void 0 : _e.VeteranStatus) === 1;
    var areAllSoleProp = customerRecords.length > 0 && customerRecords.every(isSoleProprietorship);
    var collectsGuarantorAndContact = !isConsumer && !areAllSoleProp;
    var contacts = collectsGuarantorAndContact
        ? buildContactInfo(customers, customerRecords, isNor)
        : [];
    if (!isSchedule && collectsGuarantorAndContact) {
        var guarantorStep = wizard.guarantorInformation;
        var guarantorRecords = (guarantorStep === null || guarantorStep === void 0 ? void 0 : guarantorStep.isGuarantorIncluded)
            ? (_f = guarantorStep.guarantors) !== null && _f !== void 0 ? _f : []
            : [];
        var guarantors_1 = guarantorRecords.map(function (guarantor, index) {
            return buildGuarantor(guarantor, index);
        });
        guarantorRecords.forEach(function (guarantor, index) {
            if (readGuarantorType(guarantor) !== GUARANTOR_TYPE.Business) {
                return;
            }
            var signerContact = buildGuarantorSignerContact(guarantor, guarantors_1[index]);
            if (signerContact) {
                contacts.push(signerContact);
            }
        });
        customers.forEach(function (customer, index) {
            var signerGuarantor = buildSignerGuarantor(customer, customerRecords[index], index, guarantors_1.length);
            if (signerGuarantor) {
                guarantors_1.push(signerGuarantor);
                contacts.push(buildSignerGuarantorContact(customer, signerGuarantor, isNor ? (contacts.length === 0 ? 1 : 0) : 1));
            }
        });
        if (guarantors_1.length > 0) {
            payload.GuarantorInfo = guarantors_1;
        }
    }
    if (contacts.length > 0) {
        payload.ContactInfo = contacts;
    }
    var finance = (_g = wizard.financeAndDocuments) !== null && _g !== void 0 ? _g : {};
    if (!isLineOfCredit) {
        var assets = (_h = wizard.assets) !== null && _h !== void 0 ? _h : [];
        if (assets.length > 0) {
            payload.AssetInfo = buildAssetInfo(assets, (_j = customers[0]) !== null && _j !== void 0 ? _j : {}, (_k = wizard.assetLocations) !== null && _k !== void 0 ? _k : [], isPrimaryCustomerVeteran);
        }
        applyFinanceProductCode(payload, finance, wizard.financeProducts);
        var term = Number(read(finance, FINANCE_FIELD.TermLengthMonths, 'Term') || 0);
        if (term) {
            payload.Term = term;
        }
        var isLoanProduct = payload.IsLoan === 1;
        var upfrontAmount = parseAmount(isLoanProduct
            ? readFieldValue(finance, FINANCE_FIELD.DownPayment, API_FIELD.DownPayment)
            : readFieldValue(finance, FINANCE_FIELD.AdvancePaymentAmount, 'AdvancePaymentAmount'));
        payload.DownPayment = isLoanProduct ? upfrontAmount : 0;
        payload.AdvancePaymentAmount = isLoanProduct ? 0 : upfrontAmount;
        payload.IsRental = isFlagged(finance[FINANCE_FIELD.IsAssetForRental]) ? 1 : 0;
        if ((_l = selection === null || selection === void 0 ? void 0 : selection.vendorConfig) === null || _l === void 0 ? void 0 : _l.IsSeasonalPayment) {
            payload.IsSeasonalPayment = isFlagged(finance[FINANCE_FIELD.IsSeasonalPayment]) ? 1 : 0;
        }
        if (isPrimaryCustomerVeteran) {
            payload.DownPaymentAssistance = isFlagged(finance[FINANCE_FIELD.DownPaymentAssistance])
                ? 1
                : 0;
        }
        if (isConsumer && !isLoanProduct) {
            payload.TradeInAllowance = parseAmount(readFieldValue(finance, FINANCE_FIELD.TradeInAllowance, 'TradeInAllowance'));
            payload.Rebate = parseAmount(readFieldValue(finance, FINANCE_FIELD.Rebate, 'Rebate'));
            payload.CashDown = parseAmount(readFieldValue(finance, FINANCE_FIELD.CashDown, 'CashDown'));
        }
        if (isSchedule && wizard.isRentalFleetLoc) {
            payload.IsRental = 1;
        }
    }
    else {
        var amountRequested = read(finance, 'amountRequested', 'AmountRequested');
        if (amountRequested) {
            payload.AmountRequested = parseAmount(amountRequested);
        }
        put(payload, 'PurposeOfFinance', read(finance, 'purposeOfFinance', 'PurposeOfFinance'));
        applyFinanceProductCode(payload, finance, wizard.financeProducts);
    }
    put(payload, 'ProgramAspireOId', selection === null || selection === void 0 ? void 0 : selection.programAspireOId);
    put(payload, 'ProgramId', selection === null || selection === void 0 ? void 0 : selection.programId);
    put(payload, 'SalesRepId', selection === null || selection === void 0 ? void 0 : selection.salesRepId);
    put(payload, 'SalesRepAccountAspireId', (_o = (_m = selection === null || selection === void 0 ? void 0 : selection.dealerEntries) === null || _m === void 0 ? void 0 : _m[0]) === null || _o === void 0 ? void 0 : _o.salesRepAccountAspireId);
    put(payload, 'DealerAspireOId', selection === null || selection === void 0 ? void 0 : selection.dealershipId);
    put(payload, 'LocationAspireOId', selection === null || selection === void 0 ? void 0 : selection.locationId);
    put(payload, 'LocationCode', selection === null || selection === void 0 ? void 0 : selection.locationCode);
    var additionalDealers = ((_p = selection === null || selection === void 0 ? void 0 : selection.dealerEntries) !== null && _p !== void 0 ? _p : []).slice(1).map(function (entry) {
        var additionalDealer = {};
        put(additionalDealer, 'DealerAspireOId', entry.dealershipId);
        put(additionalDealer, 'SalesRepAccountAspireId', entry.salesRepAccountAspireId);
        return additionalDealer;
    });
    if (additionalDealers.length) {
        payload.AdditionalDealers = additionalDealers;
    }
    return payload;
};
